import React from 'react'
import { Platform, Text, TouchableOpacity, View } from 'react-native'
import { useTheme, type ThemeMode } from '../../hooks/useTheme'
import { renderLogText, type NameHighlight } from '../../lib/renderLogText'
import { readablePlayerTextColor } from '../../lib/playerTextColor'
import { turnCardStyle } from '../../lib/turnPanelAppearance'
import { ATTACK_LOG_APPEARANCE, type AttackLogAppearance } from '../../lib/attackLogAppearance'
import { useTurnPanelDesktop } from './TurnControlCard'

export type { AttackLogAppearance } from '../../lib/attackLogAppearance'

/** The running combat report; used by the game and the QA design gallery. */
export function AttackLogCard({ rollCount, summary, highlights, conquered, expanded, onToggle, children, appearance, themeOverride }: {
  rollCount: number
  summary: string
  highlights: NameHighlight[]
  conquered: boolean
  expanded: boolean
  onToggle: () => void
  children?: React.ReactNode
  appearance?: AttackLogAppearance
  themeOverride?: ThemeMode
}) {
  const { theme: activeTheme } = useTheme()
  const theme = themeOverride ?? activeTheme
  const isDesktop = useTurnPanelDesktop()
  const treatment = appearance ?? ATTACK_LOG_APPEARANCE[theme]
  const surface = treatment.background
  const ink = theme === 'dark' ? '#e6e1c4' : '#18170f'
  const soft = theme === 'dark' ? '#b4b79e' : '#66694f'
  const readableHighlights = highlights.map(item => ({ ...item, color: readablePlayerTextColor(item.color, surface) }))
  return <View testID="attack-log-card" style={[
    turnCardStyle(theme, isDesktop), { gap: 5 },
    { backgroundColor: surface, borderColor: treatment.border, borderWidth: treatment.borderWidth ?? 1, borderStyle: treatment.borderStyle ?? 'solid', borderRadius: treatment.radius ?? 10 },
    treatment.accent ? { borderLeftWidth: 3, borderLeftColor: treatment.accent } : undefined,
    Platform.OS === 'web' && treatment.shadow ? { boxShadow: treatment.shadow } : undefined,
    treatment.inset ? { paddingVertical: 12 } : undefined,
  ]}>
    <TouchableOpacity onPress={onToggle} accessibilityRole="button" accessibilityState={{ expanded }}
      accessibilityLabel={`Attack log, ${rollCount} ${rollCount === 1 ? 'roll' : 'rolls'}. ${expanded ? 'Hide' : 'Show'} rolls`}
      testID="attack-log-toggle" style={{ gap: 5 }}>
      <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 8 }}>
        <Text className="font-typed" style={{ color: soft, fontSize: 11, flex: 1 }}>
          ATTACK LOG · {rollCount} {rollCount === 1 ? 'ROLL' : 'ROLLS'}
        </Text>
        {conquered ? <Text className="font-stencil" style={{ color: theme === 'dark' ? '#b3cc66' : '#46532b', fontSize: 9 }}>CONQUERED</Text> : null}
        <Text style={{ color: soft, fontSize: 12 }}>{expanded ? '▴' : '▾'}</Text>
      </View>
      <Text className="font-typed" style={{ color: ink, fontSize: 16 }} numberOfLines={expanded ? undefined : 1}>
        {renderLogText(summary, 16, readableHighlights)}
      </Text>
    </TouchableOpacity>
    {expanded ? <View style={{ gap: 5, ...(treatment.rules ? { borderTopWidth: 1, borderTopColor: treatment.border, paddingTop: 7 } : {}) }}>{children}</View> : null}
  </View>
}
